// src/merch/FaceDialog.jsx
//
// "Add a face": upload a headshot → drag / zoom it inside a head-shaped
// oval → it's cropped to that oval and placed on the product's head spot
// (product.faceAnchor[zoneId]) as a normal, movable layer.
// Browsers with the FaceDetector API (e.g. Chrome on Android) get the face
// centred automatically; everyone else just drags it into place.

import React, { useEffect, useRef, useState } from "react";
import { addLayerRaw } from "./store";
import { faceShapePath } from "./guides";
import { removeBackground } from "./removeBg";

const STAGE_TEXT = {
  loading: "Getting the face tools ready…",
  cutting: "Removing the background…",
  face: "Finding your face…",
};
import { sinkFaceUnderJersey } from "./jersey";
import { illustrate } from "./illustrate";

const BOX_W = 270;   // on-screen crop box width (height follows the shape)
const OUT_W = 480;   // output width

// crop outline: helmet face-opening (flat brim top, rounded chin) or oval
function cropPath(ctx, shape, w, h, inset, pad = 1) {
  if (shape === "free") {
    // the face box inside the padded canvas (outline only; nothing is cut)
    const bw = w / pad, bh = h / pad;
    ctx.beginPath();
    ctx.rect((w - bw) / 2, (h - bh) / 2, bw, bh);
    return;
  }
  if (shape === "helmet") faceShapePath(ctx, inset, inset, w - inset * 2, h - inset * 2);
  else { ctx.beginPath(); ctx.ellipse(w / 2, h / 2, w / 2 - inset, h / 2 - inset, 0, 0, Math.PI * 2); }
}

async function loadBitmap(file) {
  try { return await createImageBitmap(file, { imageOrientation: "from-image" }); }
  catch { return await createImageBitmap(file); }
}

export default function FaceDialog({ file, area, anchor, onClose }) {
  const shape = anchor?.shape || "oval";
  const free = shape === "free";          // helmet goes on top: no cut shape
  const PAD = free ? anchor?.pad || 1.4 : 1; // canvas = face box × PAD
  const aspect = anchor?.aspect || 0.8;            // width / height of the face shape
  const BOX_H = Math.round(BOX_W / aspect);
  const OUT_H = Math.round(OUT_W / aspect);
  const [bmp, setBmp] = useState(null);
  const [view, setView] = useState({ x: 0, y: 0, s: 1 }); // image px → box px: box = img*s + (x,y)
  const [error, setError] = useState(null);
  const [hint, setHint] = useState(
    free
      ? "Drag and zoom so the face fills the dashed box: eyebrows near the top, chin at the bottom. The helmet goes on top."
      : shape === "helmet"
      ? "Drag and zoom so the face fills the helmet — eyebrows just under the top edge, chin at the bottom. It's fine if cheeks and ears go past the sides."
      : "Drag and zoom so the face fills the oval."
  );
  const [bgRes, setBgRes] = useState(null);      // { person, face, info } from removeBackground
  const [bgState, setBgState] = useState("idle"); // idle | working | ready | failed
  const [stage, setStage] = useState({ name: "loading", p: 0.05 }); // progress while working
  const [bgMode, setBgMode] = useState("face");   // face | person | photo
  const touched = useRef(false);
  const framed = useRef(false);                   // landmarks already framed the face                  // user has moved/zoomed → don't auto-frame
  const cut = !bgRes ? null
    : bgMode === "face" ? (bgRes.face || bgRes.person)
    : bgMode === "person" ? bgRes.person : null;
  const [look, setLook] = useState(60);            // "Illustrated" 0–100
  const [styled, setStyled] = useState(null);      // filtered source
  const [styling, setStyling] = useState(false);
  // touch-up: erase leftover bits (collar, hair, background) / restore them.
  // The mask lives in PHOTO space, so it sticks to the face when you move/zoom.
  const [tool, setTool] = useState("move");   // move | erase | restore
  const [brush, setBrush] = useState(18);      // on-screen px
  const [maskRev, setMaskRev] = useState(0);
  const mask = useRef(null);                   // { c, k } canvas + photo→mask scale
  const maskUsed = useRef(false);
  const stroke = useRef(null);
  const canvasRef = useRef(null);
  const drag = useRef(null);
  const pts = useRef(new Map());

  // load + initial fit (auto-centre on a detected face when available)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const b = await loadBitmap(file);
        if (!alive) return;
        setBmp(b);
        {
          const k = Math.min(1, 1200 / Math.max(b.width, b.height));
          const c = document.createElement("canvas");
          c.width = Math.round(b.width * k); c.height = Math.round(b.height * k);
          mask.current = { c, k };
          maskUsed.current = false;
        }
        setBgState("working");
        removeBackground(b, (name, p) => { if (alive) setStage({ name, p }); })
          .then((r) => {
            if (!alive) return;
            setBgRes(r);
            if (!r.face) setBgMode("person");
            setBgState("ready");
            // auto-frame from landmarks (works on iPhone too)
            const f = r.info;
            if (f && !touched.current) {
              framed.current = true;
              const span = f.chinY - f.browY;
              if (free) {
                // inside the face box: brows ~20% down, chin at the bottom
                const bh = BOX_H / PAD, top = (BOX_H - bh) / 2;
                const s = (bh * 0.78) / span;
                setView({ s, x: BOX_W / 2 - f.cx * s, y: top + bh * 0.2 - f.browY * s });
              } else if (shape === "helmet") {
                const s = (BOX_H * 0.9) / span; // brows ~6% down, chin at the bottom
                setView({ s, x: BOX_W / 2 - f.cx * s, y: BOX_H * 0.06 - f.browY * s });
              } else {
                const s = (BOX_W * 0.75) / f.width;
                setView({ s, x: BOX_W / 2 - f.cx * s, y: BOX_H * 0.5 - ((f.browY + f.chinY) / 2) * s });
              }
              setHint("Found the face — adjust if needed.");
            }
          })
          .catch((e) => { console.warn("[face] background removal unavailable", e); if (alive) setBgState("failed"); });
        let v = (() => {
          const s = Math.max(BOX_W / b.width, BOX_H / b.height);
          return { s, x: (BOX_W - b.width * s) / 2, y: (BOX_H - b.height * s) / 2 };
        })();
        if ("FaceDetector" in window) {
          try {
            const faces = await new window.FaceDetector({ fastMode: true, maxDetectedFaces: 1 }).detect(b);
            const f = faces?.[0]?.boundingBox;
            if (f) {
              // face box → ~70% of the oval width, centred a touch high
              // helmet: narrow opening, so the face fills it edge to edge (cheeks
              // bleed under the ear guards), brim on the forehead
              const s = (BOX_W * (shape === "helmet" ? 0.92 : 0.7)) / f.width;
              v = { s, x: BOX_W / 2 - (f.x + f.width / 2) * s, y: BOX_H * (shape === "helmet" ? 0.47 : 0.46) - (f.y + f.height / 2) * s };
              setHint("Found the face — adjust if needed.");
            }
          } catch { /* not supported for this image — fine */ }
        }
        if (!framed.current && !touched.current) setView(v);
      } catch {
        setError("Couldn't open that photo. Try a JPG or PNG.");
      }
    })();
    return () => { alive = false; };
  }, [file]);

  // illustrated look (debounced; runs on the bg-removed photo when on)
  useEffect(() => {
    if (!bmp) return;
    if (bgState === "working") return; // wait so we only filter once
    const base = cut || bmp;
    if (look <= 0) { setStyled(null); return; }
    setStyling(true);
    const t = setTimeout(() => {
      try { setStyled(illustrate(base, look / 100)); }
      catch (e) { console.warn("[face] illustrate failed", e); setStyled(null); }
      setStyling(false);
    }, 120);
    return () => clearTimeout(t);
  }, [bmp, cut, look, bgState]);

  /** The photo (with erasing applied) drawn at (x, y, w, h) on a canvas of
   *  size cw×ch — or straight onto ctx when nothing has been erased. */
  function drawPhoto(ctx, src, x, y, w, h, cw, ch, pre = 1) {
    if (!maskUsed.current || !mask.current) {
      ctx.drawImage(src, x, y, w, h);
      return;
    }
    const off = document.createElement("canvas");
    off.width = Math.round(cw * pre); off.height = Math.round(ch * pre);
    const o = off.getContext("2d");
    o.scale(pre, pre);
    o.drawImage(src, x, y, w, h);
    o.globalCompositeOperation = "destination-out";
    o.drawImage(mask.current.c, x, y, w, h);
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.drawImage(off, 0, 0);
    ctx.restore();
  }

  // draw the crop box
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv || !bmp) return;
    const src = styled || cut || bmp;
    const dpr = window.devicePixelRatio || 1;
    cv.width = BOX_W * dpr; cv.height = BOX_H * dpr;
    const ctx = cv.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, BOX_W, BOX_H);
    drawPhoto(ctx, src, view.x, view.y, bmp.width * view.s, bmp.height * view.s, BOX_W, BOX_H, dpr);
    if (free) {
      // nothing is cut: just show where the face box (under the helmet) is
      ctx.save();
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 5]);
      ctx.shadowColor = "rgba(0,0,0,.5)";
      ctx.shadowBlur = 3;
      cropPath(ctx, shape, BOX_W, BOX_H, 0, PAD);
      ctx.stroke();
      ctx.restore();
      return;
    }
    // dim everything outside the face shape
    ctx.save();
    ctx.fillStyle = "rgba(20,20,20,0.6)";
    ctx.fillRect(0, 0, BOX_W, BOX_H);
    ctx.globalCompositeOperation = "destination-out";
    cropPath(ctx, shape, BOX_W, BOX_H, 6);
    ctx.fill();
    ctx.restore();
    // re-draw the photo inside the shape, then outline it
    ctx.save();
    cropPath(ctx, shape, BOX_W, BOX_H, 6);
    ctx.clip();
    if (maskUsed.current) {
      // (drawPhoto resets the transform; redraw via an offscreen copy)
      const off = document.createElement("canvas");
      off.width = cv.width; off.height = cv.height;
      const o = off.getContext("2d");
      drawPhoto(o, src, view.x * dpr, view.y * dpr, bmp.width * view.s * dpr, bmp.height * view.s * dpr, cv.width, cv.height);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.drawImage(off, 0, 0);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    } else {
      ctx.drawImage(src, view.x, view.y, bmp.width * view.s, bmp.height * view.s);
    }
    ctx.restore();
    ctx.save();
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 5]);
    cropPath(ctx, shape, BOX_W, BOX_H, 6);
    ctx.stroke();
    ctx.restore();
  }, [bmp, view, shape, BOX_H, cut, styled, maskRev]);

  const zoomBy = (f, cx = BOX_W / 2, cy = BOX_H / 2) => {
    touched.current = true;
    return setView((v) => {
      const s = Math.max(0.05, Math.min(20, v.s * f));
      const k = s / v.s;
      return { s, x: cx - (cx - v.x) * k, y: cy - (cy - v.y) * k };
    });
  };

  function local(e) {
    const r = canvasRef.current.getBoundingClientRect();
    return [((e.clientX - r.left) / r.width) * BOX_W, ((e.clientY - r.top) / r.height) * BOX_H];
  }
  function toMask([bx, by]) {
    const k = mask.current.k;
    return [((bx - view.x) / view.s) * k, ((by - view.y) / view.s) * k];
  }
  function paintMask(p0, p1) {
    const m = mask.current;
    if (!m) return;
    const x = m.c.getContext("2d");
    x.save();
    x.globalCompositeOperation = tool === "erase" ? "source-over" : "destination-out";
    x.strokeStyle = x.fillStyle = "#000";
    x.lineCap = "round";
    x.lineWidth = (brush / view.s) * m.k;
    x.beginPath();
    x.moveTo(p0[0], p0[1]);
    x.lineTo(p1[0] + 0.01, p1[1]);
    x.stroke();
    x.restore();
    maskUsed.current = true;
    setMaskRev((n) => n + 1);
  }
  function onDown(e) {
    touched.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pts.current.set(e.pointerId, local(e));
    const arr = [...pts.current.values()];
    // touch-up tools: one finger paints, a second finger switches to pinch-zoom
    if (tool !== "move" && arr.length === 1) {
      const p = toMask(local(e));
      stroke.current = p;
      paintMask(p, p);
      drag.current = null;
      return;
    }
    stroke.current = null;
    drag.current = arr.length === 2
      ? { pinch: true, d0: Math.hypot(arr[1][0] - arr[0][0], arr[1][1] - arr[0][1]), v0: view }
      : { p0: local(e), v0: view };
  }
  function onMove(e) {
    if (stroke.current && pts.current.size === 1 && pts.current.has(e.pointerId)) {
      const p = toMask(local(e));
      paintMask(stroke.current, p);
      stroke.current = p;
      return;
    }
    if (!pts.current.has(e.pointerId) || !drag.current) return;
    pts.current.set(e.pointerId, local(e));
    const d = drag.current;
    const arr = [...pts.current.values()];
    if (d.pinch && arr.length >= 2) {
      const dist = Math.hypot(arr[1][0] - arr[0][0], arr[1][1] - arr[0][1]);
      const cx = (arr[0][0] + arr[1][0]) / 2, cy = (arr[0][1] + arr[1][1]) / 2;
      const s = Math.max(0.05, Math.min(20, d.v0.s * (dist / (d.d0 || 1))));
      const k = s / d.v0.s;
      setView({ s, x: cx - (cx - d.v0.x) * k, y: cy - (cy - d.v0.y) * k });
    } else if (!d.pinch) {
      const p = local(e);
      setView({ ...d.v0, x: d.v0.x + p[0] - d.p0[0], y: d.v0.y + p[1] - d.p0[1] });
    }
  }
  function onUp(e) {
    pts.current.delete(e.pointerId);
    if (pts.current.size === 0) { drag.current = null; stroke.current = null; }
  }

  function place() {
    const out = document.createElement("canvas");
    out.width = OUT_W; out.height = OUT_H;
    const ctx = out.getContext("2d");
    const k = OUT_W / BOX_W;
    const src = styled || cut || bmp;
    drawPhoto(ctx, src, view.x * k, view.y * k, bmp.width * view.s * k, bmp.height * view.s * k, OUT_W, OUT_H);
    if (!free) {
      // crop to the face shape (a hair of blur softens the edge on the art)
      ctx.globalCompositeOperation = "destination-in";
      ctx.save();
      ctx.shadowColor = "#000";
      ctx.shadowBlur = 3;
      cropPath(ctx, shape, OUT_W, OUT_H, 6 * k);
      ctx.fill();
      ctx.restore();
    }

    const aw = area.x1 - area.x0, ah = area.y1 - area.y0;
    const a = anchor || { x: 0.5, y: 0.4, w: 0.35 };
    // image is taller than wide → contained by its height, so scale = face height
    const faceW = a.w * aw * PAD; // free mode: the padded canvas around the face box
    addLayerRaw({
      name: "Face",
      url: out.toDataURL("image/png"),
      kind: "face", // reviewed like an uploaded photo
      transform: { tx: area.x0 + a.x * aw, ty: area.y0 + a.y * ah, scale: faceW * (OUT_H / OUT_W), rot: 0 },
      // (helmet shape: flat top meets the helmet brim in the jersey art)
    });
    sinkFaceUnderJersey(); // jersey art on top: the face shows through the helmet
    onClose();
  }

  return (
    <div className="ma-modal" onPointerDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ma-modal-card ma-face" role="dialog" aria-label="Add a face">
        <div className="ma-modal-head">
          <b>Add a face</b>
          <button className="ma-btn ghost" onClick={onClose} aria-label="Close">✕</button>
        </div>
        {error && <div className="ma-note error">{error}</div>}
        {!bmp && !error && <p className="ma-muted">Opening photo…</p>}
        {bmp && (
          <>
            <p className="ma-muted">{hint}</p>
            <div className="ma-face-stage">
              <canvas
                ref={canvasRef}
                className={cut ? "ma-face-checker" : undefined}
                style={{ width: BOX_W, height: BOX_H, touchAction: "none", cursor: tool === "move" ? "grab" : "crosshair" }}
                onPointerDown={onDown}
                onPointerMove={onMove}
                onPointerUp={onUp}
                onPointerCancel={onUp}
                onWheel={(e) => { const [x, y] = local(e); zoomBy(e.deltaY < 0 ? 1.08 : 1 / 1.08, x, y); }}
              />
              {(bgState === "working" || styling) && (
                <div className={`ma-face-busy ${bgState === "working" ? "" : "light"}`} role="status" aria-live="polite">
                  <span className="ma-spinner" aria-hidden="true" />
                  <span>{bgState === "working" ? STAGE_TEXT[stage.name] || "Working…" : "Applying the look…"}</span>
                  {bgState === "working" && (
                    <i className="ma-face-progress" aria-hidden="true"><b style={{ width: `${Math.round(stage.p * 100)}%` }} /></i>
                  )}
                </div>
              )}
            </div>
            <div className="ma-face-tools">
              <div className="ma-seg small" role="group" aria-label="Tool">
                <button aria-pressed={tool === "move"} onClick={() => setTool("move")}>Move</button>
                <button aria-pressed={tool === "erase"} onClick={() => setTool("erase")} title="Rub out leftover bits (collar, hair, background)">Erase</button>
                <button aria-pressed={tool === "restore"} onClick={() => setTool("restore")} title="Paint back anything you erased">Restore</button>
              </div>
              {tool !== "move" && (
                <label className="ma-face-brush">
                  <input type="range" min={6} max={48} step={1} value={brush} onChange={(e) => setBrush(Number(e.target.value))} aria-label="Brush size" />
                  <i style={{ width: brush * 0.6, height: brush * 0.6 }} />
                </label>
              )}
            </div>
            {tool !== "move" && <p className="ma-muted ma-face-tip">One finger {tool === "erase" ? "erases" : "restores"} · two fingers still zoom and move.</p>}
            <label className="ma-paint-size">
              Zoom
              <input type="range" min={-1.5} max={2.5} step={0.01} value={Math.log(view.s / Math.max(BOX_W / bmp.width, BOX_H / bmp.height)) / Math.LN2}
                onChange={(e) => {
                  const base = Math.max(BOX_W / bmp.width, BOX_H / bmp.height);
                  const target = base * Math.pow(2, Number(e.target.value));
                  zoomBy(target / view.s);
                }} />
              <span />
            </label>
            {bgState === "ready" && (
              <div className="ma-seg small ma-face-bg" role="group" aria-label="Background">
                {bgRes?.face && <button aria-pressed={bgMode === "face"} onClick={() => setBgMode("face")}>Face only</button>}
                <button aria-pressed={bgMode === "person"} onClick={() => setBgMode("person")}>No background</button>
                {!free && <button aria-pressed={bgMode === "photo"} onClick={() => setBgMode("photo")}>Full photo</button>}
              </div>
            )}
            <label className="ma-face-slider">
              <span className="ma-face-slider-head">
                <span>Illustrated look</span>
                <span>{styling ? "…" : look ? `${look}` : "Off"}</span>
              </span>
              <input type="range" min={0} max={100} step={5} value={look}
                onChange={(e) => setLook(Number(e.target.value))} />
            </label>
            <p className="ma-muted">Tip: a front-on photo with good light works best. Faces are checked by our team before printing.</p>
            <div className="ma-modal-foot">
              <button className="tt-btn tt-btn--danger" onClick={onClose}>Cancel</button>
              <button className="tt-btn tt-btn--go" onClick={place}>Add to design</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
